import React from 'react';
import PropTypes from 'prop-types';
import * as immutable from 'immutable';
import {List} from 'immutable';
import classNames from 'classnames';
import AdminFilterWordProfileActions from 'actions/admin/AdminFilterWordProfileActions';
import './TeamListItem.styl';
import InlineProfileEditor from 'components/admin/topics/InlineProfileEditor';
import Team from 'records/Team';
import DepartmentWithTeams from 'records/admin/topics/DepartmentWithTeams';
import NewFilterWordProfilesInAccount from 'records/admin/topics/NewFilterWordProfilesInAccount';
import NewFilterWordProfile from 'records/admin/topics/NewFilterWordProfile';
import InputField from 'components/pure/utils/form/InputField';
import AdminFilterWordProfilePageActions from 'actions/admin/AdminFilterWordProfilePageActions';
import {ProfileEditorKey} from 'stores/admin/AdminFilterWordProfilePageStore';
import ToggleExpand from 'components/admin/topics/ToggleExpand';
import AutoComplete from '@components/auto-complete';
import {getMuiV4Theme} from 'utils/css/themeUtils';

const DEFAULT_TEAM_PROFILE_PLACEHOLDER = {
  title: 'Default Profile for Department',
  value: -1, // not a valid profile ID.
};

const CREATE_NEW_PROFILE_PLACEHOLDER = {
  title: 'Create New',
  value: -2, // not a valid profile ID.
};

/**
 * Get the list of options for the profile dropdown.
 *
 * @param profilesInAccount {NewFilterWordProfilesInAccount} all available profiles.
 * @param editable {boolean} if true, include the "create new" option in the list.
 * @param isDefaultForDepartment {boolean} if false, include the default profile for department
 * option in the list.
 *
 * @return {({title: string, value: number})[]} array of options.
 */
function profileOptions(profilesInAccount, editable, isDefaultForDepartment) {
  const baseOptions = profilesInAccount.profiles.map(profile => {
    return {
      title: profile.name,
      value: profile.filterWordProfileId,
    };
  });
  // Only add the default profile option to actual teams.
  const maybeWithDefault = isDefaultForDepartment
    ? baseOptions
    : [DEFAULT_TEAM_PROFILE_PLACEHOLDER, ...baseOptions];
  // Only add the new profile placeholder if the user can actually edit.
  return editable ? [...maybeWithDefault, CREATE_NEW_PROFILE_PLACEHOLDER] : [...maybeWithDefault];
}

/**
 * Render a dropdown option.
 *
 * @param option {{title: string, value: number}} the option to render.
 * @return {JSX.Element}
 */
function renderOption(option) {
  if (option.value === CREATE_NEW_PROFILE_PLACEHOLDER.value) {
    return (
      <div className="simple-option" style={{color: getMuiV4Theme().palette.primary.dark}}>
        {option.title}
      </div>
    );
  } else {
    return <div>{option.title}</div>;
  }
}

/**
 * Is the given profile valid?
 *
 * @param profile {NewFilterWordProfile} the profile to validate
 * @param reservedProfileNames {Set<string>} names of other profiles in the account. We don't want duplicate
 * profile names, so, if the profile has one of these names, it is invalid.
 * @return {boolean} true iff the given profile is valid.
 */
function isValid(profile, reservedProfileNames) {
  return Boolean(profile.name && !reservedProfileNames.has(profile.name));
}

/**
 * Each item represents either a team in a department, or is a dummy team representing the
 * department itself, and has a filter word profile selector dropdown and inline editor.
 */
export default class TeamListItem extends React.PureComponent {
  static propTypes = {
    department: PropTypes.instanceOf(DepartmentWithTeams).isRequired,
    editable: PropTypes.bool.isRequired,
    expanded: PropTypes.bool.isRequired,
    profileDepartmentAndTeamUsage: PropTypes.instanceOf(immutable.Map).isRequired,
    profileEditorKey: PropTypes.instanceOf(ProfileEditorKey),
    profilesInAccount: PropTypes.instanceOf(NewFilterWordProfilesInAccount).isRequired,
    idToProfileMap: PropTypes.instanceOf(immutable.Map).isRequired,
    isDefaultForDepartment: PropTypes.bool,
    team: PropTypes.instanceOf(Team).isRequired,
    topicsInAccount: PropTypes.instanceOf(immutable.List).isRequired,
  };

  constructor(props) {
    super(props);
    this.state = {
      editingNewProfile: false,
      nameIsValid: false,
      profileForEditing: undefined,
      reservedProfileNames: List(), // List<String>
      viewingFilterWordProfileId: undefined,
    };
  }

  toggleExpanded(teamId) {
    AdminFilterWordProfilePageActions.toggleTeam(teamId);
  }

  updateEditedProfileName(name) {
    const {profileForEditing, reservedProfileNames} = this.state;
    const newProfileForEditing = profileForEditing.set('name', name);
    this.setState(
      {
        nameIsValid: isValid(newProfileForEditing, reservedProfileNames),
        profileForEditing: newProfileForEditing,
      },
      AdminFilterWordProfilePageActions.markProfileEditorDirty
    );
  }

  /** Allows the inline editor to edit the profile, if there is one. */
  updateEditedProfile(profile) {
    this.setState(
      {
        profileForEditing: profile,
      },
      AdminFilterWordProfilePageActions.markProfileEditorDirty
    );
  }

  startEditor(profile, departmentId, teamId, editingNewProfile, reservedProfileNames) {
    this.setState(
      {
        editingNewProfile,
        nameIsValid: isValid(profile, reservedProfileNames),
        profileForEditing: profile,
        reservedProfileNames,
      },
      () =>
        AdminFilterWordProfilePageActions.startProfileEditor(
          departmentId,
          teamId,
          editingNewProfile // new profiles start dirty. others do not.
        )
    );
  }

  clearEditor() {
    this.setState(
      {
        editingNewProfile: false,
        nameIsValid: true,
        profileForEditing: undefined,
        reservedProfileNames: List(),
      },
      AdminFilterWordProfilePageActions.stopProfileEditor.defer
    );
  }

  saveProfile(departmentId, teamId) {
    const {isDefaultForDepartment} = this.props;
    const {profileForEditing, editingNewProfile} = this.state;
    if (editingNewProfile) {
      AdminFilterWordProfileActions.createProfile(
        profileForEditing,
        departmentId,
        !isDefaultForDepartment ? teamId : null
      );
      this.setState({
        editingNewProfile: false,
      });
    } else {
      AdminFilterWordProfileActions.updateProfile(profileForEditing);
    }
  }

  saveKeywordsToProfile(newKeywords) {
    const {profileForEditing} = this.state;
    this.setState(
      {
        profileForEditing: profileForEditing.replaceKeywords(newKeywords),
      },
      AdminFilterWordProfilePageActions.markProfileEditorDirty
    );
  }

  changeAssociation(filterWordProfileId, departmentId, teamId, reservedProfileNames) {
    const {editable, isDefaultForDepartment} = this.props;
    if (!editable) {
      // Not editable => set state so user can view this profile. Do not continue to actions, which
      // try to make actual changes but will fail server-side. The option to create new isn't
      // available in this mode, so no need to deal with it here.
      this.setState({
        viewingFilterWordProfileId: filterWordProfileId,
      });
      return;
    }
    if (filterWordProfileId === CREATE_NEW_PROFILE_PLACEHOLDER.value) {
      this.startEditor(
        new NewFilterWordProfile(),
        departmentId,
        teamId,
        true,
        reservedProfileNames
      );
      return;
    }
    // If this is the special "team" representing the department's default profile, we adjust the
    // department. Otherwise it's an actual team we need to change.
    if (isDefaultForDepartment) {
      if (filterWordProfileId == null) {
        AdminFilterWordProfileActions.dissociateDepartmentProfile(departmentId);
      } else {
        AdminFilterWordProfileActions.associateProfileWithDepartment(
          filterWordProfileId,
          departmentId
        );
      }
    } else {
      // need dept id to find the team efficiently in the store for these team calls.
      if (
        filterWordProfileId === DEFAULT_TEAM_PROFILE_PLACEHOLDER.value || // explicitly selected the fake profile
        filterWordProfileId == null // clicked the X to remove any selection
      ) {
        AdminFilterWordProfileActions.dissociateTeamProfile(departmentId, teamId);
      } else {
        AdminFilterWordProfileActions.associateProfileWithTeam(
          filterWordProfileId,
          departmentId,
          teamId
        );
      }
    }
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    if (
      // Clear editor after create action completes successfully.
      prevProps.profilesInAccount !== this.props.profilesInAccount ||
      // Clear editor if the global state has changed (presumably a different editor opened up).
      (prevProps.profileEditorKey && prevProps.profileEditorKey !== this.props.profileEditorKey)
    ) {
      this.clearEditor();
    }
  }

  render() {
    const {
      department,
      editable,
      expanded,
      idToProfileMap,
      isDefaultForDepartment,
      profileDepartmentAndTeamUsage,
      profileEditorKey,
      profilesInAccount,
      team,
      topicsInAccount,
    } = this.props;
    const {editingNewProfile, nameIsValid, profileForEditing, viewingFilterWordProfileId} =
      this.state;

    const filterWordProfileId =
      viewingFilterWordProfileId || team.filterWordProfileId || department.filterWordProfileId;
    const profile =
      profileForEditing || (filterWordProfileId && idToProfileMap.get(filterWordProfileId));
    if (!profile && !isDefaultForDepartment && filterWordProfileId) {
      console.error(`No profile found for id ${filterWordProfileId}`); // Shouldn't happen.
    }
    const profileNames = profilesInAccount.profiles.map(p => p.name).toSet();
    // Build the list of names we won't allow this profile to have for validation purposes.
    // If we're editing a new profile or if the profile isn't known to the server (almost certainly
    // because it's the dummy profile representing the default for the department), none of the
    // existing names are allowed. Otherwise, we need to allow this profile to have its own name
    // (in case the user edits and undoes the edit).
    const reservedProfileNames =
      editingNewProfile || !profile ? profileNames : profileNames.delete(profile.name);

    const editing = ProfileEditorKey.matches(profileEditorKey, department.departmentId, team.id);
    // Don't allow new editors to open if one is already open.
    const thisIsEditable = editable && (!profileEditorKey || editing);

    return (
      <div className="team-list-item">
        <div className="team-name">{team.name}</div>
        <div className="profile">
          <div className={classNames('profile-toggle', {hidden: !profile})}>
            <ToggleExpand
              expanded={expanded || editingNewProfile}
              onToggle={() => this.toggleExpanded(team.id)}
            />
          </div>
          <div className="drop-down">
            {profileForEditing ? (
              <div className="new-profile">
                <InputField
                  value={profileForEditing.name}
                  placeholder="Enter Keyword/Topic Profile Name"
                  onChange={name => this.updateEditedProfileName(name, reservedProfileNames)}
                />
              </div>
            ) : (
              <AutoComplete
                placeholder="Select a profile"
                value={
                  team.filterWordProfileId ||
                  // Only fallback to the default for actual teams.
                  (!isDefaultForDepartment && DEFAULT_TEAM_PROFILE_PLACEHOLDER.value)
                }
                options={profileOptions(profilesInAccount, editable, isDefaultForDepartment)}
                renderOption={renderOption}
                loading={false}
                onChange={profileId =>
                  this.changeAssociation(
                    profileId,
                    department.departmentId,
                    team.id,
                    reservedProfileNames
                  )
                }
              />
            )}
            {(expanded || editingNewProfile) && (
              <InlineProfileEditor
                editable={thisIsEditable}
                editing={editing}
                nameIsValid={nameIsValid}
                onCancel={() => this.clearEditor()}
                onChangeProfile={profile => this.updateEditedProfile(profile)}
                onSave={() => this.saveProfile(department.departmentId, team.id)}
                onSaveKeywords={newKeywords => this.saveKeywordsToProfile(newKeywords)}
                onStartEditor={(profile, isNew) =>
                  this.startEditor(
                    profile,
                    department.departmentId,
                    team.id,
                    isNew,
                    reservedProfileNames
                  )
                }
                profile={profile}
                profileDepartmentAndTeamUsage={profileDepartmentAndTeamUsage}
                topicsInAccount={topicsInAccount}
              />
            )}
          </div>
        </div>
      </div>
    );
  }
}
